import React, { useContext, useEffect, useState, useCallback } from "react";
import api from "../utils/axios";
import { AuthContext } from "../context/AuthContext";
import { Box, Heading, Flex, Button, IconButton, useDisclosure, useBreakpointValue } from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import ErrorMessage from "../components/onlinemenu/ErrorMessage";
import LoadingSpinner from "../components/onlinemenu/LoadingSpinner";
import DeactivateModal from "../components/expirationcheck/DeactivateModal";
import EditModal from "../components/expirationcheck/EditModal";
import CategoryList from "../components/expirationcheck/CategoryList";

const ExpirationCheckPage = () => {
    const { currLocation } = useContext(AuthContext);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isDeactivateOpen,
        onOpen: onDeactivateOpen,
        onClose: onDeactivateClose,
    } = useDisclosure();
    const [editData, setEditData] = useState(null);
    const [editType, setEditType] = useState("");
    const [deactivateData, setDeactivateData] = useState(null);
    const isMobile = useBreakpointValue({ base: true, md: false });

    const fetchCategories = useCallback(async () => {
        if (!currLocation) return;
        try {
            setLoading(true);
            const response = await api.get(`expiration-checks/categories/location/${currLocation}`);
            setCategories(response.data);
            setError(null);
        } catch (err) {
            setError("Failed to load expiration checks.");
        } finally {
            setLoading(false);
        }
    }, [currLocation]);

    useEffect(() => {
        if (!currLocation) return;
        fetchCategories();
    }, [currLocation, fetchCategories]);

    const openEditModal = useCallback((type, data) => {
        setEditData(data);
        setEditType(type);
        onOpen();
    }, [onOpen]);

    const openDeactivateModal = useCallback((type, data) => {
        setDeactivateData({ type, data });
        onDeactivateOpen();
    }, [onDeactivateOpen]);

    const handleSave = async (formData) => {
        try {
            const editingId = formData.id !== undefined && formData.id !== 0 ? formData.id : 0;
            const method = editingId ? "put" : "post";
            let apiEndpoint = "";
            let body = formData;

            if (editType === "category") {
                apiEndpoint = editingId
                    ? `expiration-checks/categories/${editingId}/location/${currLocation}`
                    : `expiration-checks/categories/location/${currLocation}`;
                body = { name: formData.name, notifyDaysBefore: formData.notifyDaysBefore };
            } else if (editType === "property") {
                // categoryId is never shown/edited in the form - read it from the
                // editData captured when the modal was opened, not from formData,
                // so it can't be lost through the modal's own input state.
                const categoryId = editData?.categoryId ?? formData.categoryId;
                apiEndpoint = editingId
                    ? `expiration-checks/properties/${editingId}/location/${currLocation}`
                    : `expiration-checks/properties/location/${currLocation}`;
                body = editingId ? { name: formData.name } : { name: formData.name, categoryId };
            } else if (editType === "entry") {
                // Same reasoning as categoryId above, for propertyId.
                const propertyId = editData?.propertyId ?? formData.propertyId;
                apiEndpoint = editingId
                    ? `expiration-checks/entries/${editingId}/location/${currLocation}`
                    : `expiration-checks/entries/location/${currLocation}`;
                body = editingId
                    ? { subjectName: formData.subjectName, expirationDate: formData.expirationDate }
                    : { subjectName: formData.subjectName, expirationDate: formData.expirationDate, propertyId };
            }

            const response = await api[method](apiEndpoint, body);

            if (editType === "category" && editingId) {
                setCategories((prev) =>
                    prev.map((category) =>
                        category.id === editingId ? { ...category, ...formData, ...response.data } : category
                    )
                );
            } else if (editType === "category" && !editingId) {
                const savedCategory = { isActive: true, properties: [], ...formData, ...response.data };
                setCategories((prev) => [...prev, { ...savedCategory, properties: savedCategory.properties || [] }]);
                // Self-heal if the create response didn't echo back an id -
                // the local copy would otherwise be unusable for follow-up actions.
                if (!savedCategory.id) fetchCategories();
            } else if (editType === "property" && editingId) {
                const savedProperty = { ...formData, ...response.data };
                setCategories((prev) =>
                    prev.map((category) => {
                        if (!category.properties.some((property) => property.id === editingId)) return category;
                        return {
                            ...category,
                            properties: category.properties.map((property) =>
                                property.id === editingId ? { ...property, ...savedProperty, entries: property.entries } : property
                            ),
                        };
                    })
                );
            } else if (editType === "property" && !editingId) {
                const targetCategoryId = editData?.categoryId ?? formData.categoryId;
                const savedProperty = { isActive: true, entries: [], categoryId: targetCategoryId, ...formData, ...response.data };
                setCategories((prev) =>
                    prev.map((category) =>
                        category.id === targetCategoryId
                            ? { ...category, properties: [...category.properties, { ...savedProperty, entries: savedProperty.entries || [] }] }
                            : category
                    )
                );
                if (!savedProperty.id) fetchCategories();
            } else if (editType === "entry" && editingId) {
                const savedEntry = { ...formData, ...response.data };
                setCategories((prev) =>
                    prev.map((category) => ({
                        ...category,
                        properties: category.properties.map((property) => {
                            if (!property.entries.some((entry) => entry.id === editingId)) return property;
                            return {
                                ...property,
                                entries: property.entries.map((entry) => (entry.id === editingId ? savedEntry : entry)),
                            };
                        }),
                    }))
                );
            } else if (editType === "entry" && !editingId) {
                const targetPropertyId = editData?.propertyId ?? formData.propertyId;
                const savedEntry = { isActive: true, propertyId: targetPropertyId, ...formData, ...response.data };
                setCategories((prev) =>
                    prev.map((category) => ({
                        ...category,
                        properties: category.properties.map((property) =>
                            property.id === targetPropertyId
                                ? { ...property, entries: [...property.entries, savedEntry] }
                                : property
                        ),
                    }))
                );
                if (!savedEntry.id) fetchCategories();
            }

            setEditData(null);
            onClose();
        } catch (err) {
            console.error("Failed to save expiration check:", err);
        }
    };

    const handleDeactivate = async () => {
        try {
            const { type, data } = deactivateData || {};
            const fallbackIsActive = !data.isActive;

            if (type === "category") {
                const response = await api.put(`expiration-checks/categories/deactivate/${data.id}`);
                const isActive = response.data?.isActive ?? fallbackIsActive;
                setCategories((prev) =>
                    prev.map((category) => (category.id === data.id ? { ...category, isActive } : category))
                );
            } else if (type === "property") {
                const response = await api.put(`expiration-checks/properties/deactivate/${data.id}`);
                const isActive = response.data?.isActive ?? fallbackIsActive;
                setCategories((prev) =>
                    prev.map((category) => ({
                        ...category,
                        properties: category.properties.map((property) =>
                            property.id === data.id ? { ...property, isActive } : property
                        ),
                    }))
                );
            } else if (type === "entry") {
                const response = await api.put(`expiration-checks/entries/deactivate/${data.id}`);
                const isActive = response.data?.isActive ?? fallbackIsActive;
                setCategories((prev) =>
                    prev.map((category) => ({
                        ...category,
                        properties: category.properties.map((property) => ({
                            ...property,
                            entries: property.entries.map((entry) =>
                                entry.id === data.id ? { ...entry, isActive } : entry
                            ),
                        })),
                    }))
                );
            }

            setDeactivateData(null);
            onDeactivateClose();
        } catch (err) {
            console.error("Failed to update active state:", err);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <ErrorMessage message={error} />;
    }

    return (
        <Box
            as="main"
            pt={{ base: "24px", md: "20px" }}
            pl={{ base: "0px", md: "240px" }}
            pr={{ base: "0px", md: "30px" }}
            pb="20px"
            minH="100vh"
        >
            <Flex justify="space-between" align="center" mb={6} px={4} pt={4}>
                <Heading color="brand.700" size="lg">Provjera isteka</Heading>
                {isMobile ? (
                    <IconButton
                        icon={<FaPlus />}
                        aria-label="Dodaj kategoriju"
                        bg="brand.600"
                        color="white"
                        borderRadius="full"
                        onClick={() => openEditModal("category", { name: "", notifyDaysBefore: 14 })}
                        _hover={{ bg: "brand.700" }}
                    />
                ) : (
                    <Button
                        leftIcon={<FaPlus />}
                        bg="brand.600"
                        color="white"
                        borderRadius="full"
                        onClick={() => openEditModal("category", { name: "", notifyDaysBefore: 14 })}
                        _hover={{ bg: "brand.700" }}
                    >
                        Dodaj kategoriju
                    </Button>
                )}
            </Flex>

            <Box px={4}>
                <CategoryList categories={categories} onEdit={openEditModal} onDeactivate={openDeactivateModal} />
            </Box>

            <EditModal
                isOpen={isOpen}
                onClose={onClose}
                onSave={handleSave}
                editData={editData}
                editType={editType}
            />

            <DeactivateModal
                isOpen={isDeactivateOpen}
                onClose={onDeactivateClose}
                onConfirm={handleDeactivate}
                data={deactivateData}
            />
        </Box>
    );
};

export default ExpirationCheckPage;
